import { Stack, Typography } from '@mui/material';
import { DEFAULT_BRAND_COLOR } from '@provision/landing-page-ui';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { SelectMenu } from 'src/components/SelectMenu';
import { ColorPickerField } from 'src/pages/Settings/memberships/components/color-picker-field';
import { ImageUrlField } from 'src/pages/Settings/memberships/components/landing-page-tab/block-fields/image-url-field';
import {
  BACKGROUND_OPTIONS,
  blockFieldName,
  type LandingPageFormValues,
} from 'src/pages/Settings/memberships/components/landing-page-tab/types';
import { panelFieldLabelSx } from 'src/pages/Settings/memberships/styles';
import { COLOR_FIELD_RULES } from 'src/pages/Settings/memberships/utils/color-field-rules';

const COLOR_RULES = {
  validate: {
    required: (value: string) =>
      value.trim() !== '' || 'Pick a color, or set the background to None',
    color: COLOR_FIELD_RULES.validate,
  },
};

interface BlockBackgroundFieldsProps {
  index: number;
  readOnly: boolean;
}

export const BlockBackgroundFields = ({ index, readOnly }: BlockBackgroundFieldsProps) => {
  const { control, getValues, setValue } = useFormContext<LandingPageFormValues>();
  const fillName = blockFieldName(index, 'backgroundFill');
  const colorName = blockFieldName(index, 'backgroundColor');
  const fill = useWatch({ control, name: fillName });

  // A color fill with no color would render nothing, so the first pick starts from the brand.
  const prefillColor = (next: string) => {
    if ((next === 'solid' || next === 'autoGradient') && !getValues(colorName).trim()) {
      const primary = getValues('settings.primaryColor').trim();
      setValue(colorName, primary || DEFAULT_BRAND_COLOR, { shouldDirty: true });
    }
  };

  return (
    <Stack gap={1.5}>
      <Stack gap={1}>
        <Typography variant="caption" sx={panelFieldLabelSx}>
          Background
        </Typography>
        <Controller
          name={fillName}
          control={control}
          render={({ field }) => (
            <SelectMenu
              label="Background"
              options={BACKGROUND_OPTIONS}
              value={field.value}
              onChange={(next) => {
                field.onChange(next);
                prefillColor(next);
              }}
              disabled={readOnly}
              menuWidth="trigger"
              sx={{ width: '100%' }}
            />
          )}
        />
      </Stack>

      {fill === 'solid' || fill === 'autoGradient' ? (
        <Controller
          name={colorName}
          control={control}
          rules={COLOR_RULES}
          render={({ field, fieldState }) => (
            <ColorPickerField
              field={field}
              label="Color"
              readOnly={readOnly}
              hasError={Boolean(fieldState.error)}
            />
          )}
        />
      ) : null}

      {fill === 'image' ? (
        <ImageUrlField
          index={index}
          prefix="background"
          label="Background image URL"
          helperText="A wash in the block's own tone keeps text readable over the image."
          readOnly={readOnly}
        />
      ) : null}
    </Stack>
  );
};
